/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { ActionType, ComponentType, ScreenType } from "../enums";
import { Identifier } from "../models";
import {
  IApplicationMenuItemComponentModel,
  IComponentModel,
  IConfigurationModel,
  IScreenModel,
} from "../models/Configuration";

export class ConfigurationHelper {
  static getScreenByType(
    configuration: IConfigurationModel | undefined,
    screenType: ScreenType,
  ) {
    let screen: IScreenModel = {};

    if (configuration && configuration.Screens) {
      screen = configuration.Screens[screenType];
    }

    return screen;
  }

  static getScreenRouteKey(screen: IScreenModel) {
    switch (screen.ScreenTypeCode) {
      case ScreenType.Custom:
        return `${screen.Id}`;
      default:
        return `${screen.ScreenTypeCode?.toLowerCase()}`;
    }
  }

  static getScreenByRouteKey(
    configuration: IConfigurationModel | undefined,
    routeKey: string,
  ): IScreenModel | undefined {
    let screen: IScreenModel | undefined = undefined;
    const customScreens = configuration?.Screens?.CUSTOM;

    if (configuration && configuration.Screens) {
      screen = configuration.Screens[routeKey.toUpperCase()];

      if (!screen && customScreens) {
        screen = customScreens[routeKey];
      }
    }

    if (!screen && customScreens) {
      Object.keys(customScreens).forEach((key) => {
        if (customScreens[key].RouteName === routeKey) {
          screen = customScreens[key];
        }
      });
    }

    return screen;
  }

  static getScreenById(
    configuration: IConfigurationModel | undefined,
    screenId: number,
  ): IScreenModel | undefined {
    const scrrenKey = `${screenId}`;

    return this.getScreenByRouteKey(configuration, scrrenKey);
  }

  static getApplicationMenuItemScreenKey(
    component: Pick<IApplicationMenuItemComponentModel, "Action">,
  ) {
    switch (component?.Action?.ScreenTypeCode) {
      case ScreenType.PersonDetails:
        let screenPath: string = "";
        const screenPathParts =
          component?.Action?.ScreenTypeCode?.toLowerCase()?.split("_");

        if (screenPathParts && screenPathParts.length > 0) {
          screenPath = screenPathParts[0];
        }

        if (component?.Action?.ScreenId) {
          screenPath += `/${component?.Action?.ScreenId}`;
        }

        return screenPath;
      case ScreenType.LiveDetails:
      case ScreenType.MatchDetails:
      case ScreenType.EventDetails:
      case ScreenType.MovieDetails:
      case ScreenType.SeriesDetails:
      case ScreenType.PackageDetails:
      case ScreenType.PodcastDetails:
        let screenKey = `${component?.Action?.ScreenTypeCode?.toLowerCase().replace(
          "_",
          "-",
        )}`;

        if (component?.Action?.ScreenId) {
          screenKey += `/${component?.Action?.ScreenId}`;
        }

        return screenKey;
      default:
        return component?.Action?.ActionType === ActionType.OpenScreen
          ? component?.Action?.ScreenId
            ? `${component?.Action?.ScreenId}`
            : `${component?.Action?.ScreenTypeCode?.toLowerCase().replace(
                "_",
                "-",
              )}`
          : "";
    }
  }

  static getComponentById<T extends IComponentModel = IComponentModel>(
    configuration: IConfigurationModel | undefined,
    componentId: Identifier,
    componentType: ComponentType,
  ): T | undefined {
    if (
      !configuration?.Screens ||
      componentId === undefined ||
      componentId === null
    ) {
      return undefined;
    }

    const screens: IScreenModel[] = [];

    Object.entries(configuration.Screens).forEach(([key, screenValue]) => {
      if (key === "CUSTOM") {
        screens.push(
          ...Object.values(screenValue as Record<string, IScreenModel>),
        );
        return;
      }

      screens.push(screenValue as IScreenModel);
    });

    for (const screen of screens) {
      const components = screen.Components;

      if (!Array.isArray(components)) {
        continue;
      }

      const component = components.find(
        (item) =>
          item.Id === componentId && item.ComponentTypeCode === componentType,
      );

      if (component) {
        return component as T;
      }
    }

    return undefined;
  }

  static getScreenPathByType(screenTypeCode?: ScreenType): string {
    if (!screenTypeCode) {
      return "";
    }

    switch (screenTypeCode) {
      case ScreenType.PersonDetails:
        const screenPathParts = screenTypeCode?.toLowerCase()?.split("_");

        if (screenPathParts && screenPathParts.length > 0) {
          return screenPathParts[0];
        }

        return `${screenTypeCode?.toLowerCase().replace("_", "-")}`;
      default:
        return `${screenTypeCode?.toLowerCase().replace("_", "-")}`;
    }
  }
}
